import {
  DateFormat,
  EmptySectionCard,
  TreatmentEpisodeCard,
} from '@equip.health/ui';

import { TreatmentEpisodeStatus } from '~/lib/constants/patients.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { toLocalMidnight } from '~/lib/util/date.util';
import {
  getDiffInWeeks,
  levelTagColorMapper,
  treatmentEpisodeColorMapper,
} from '~/lib/util/patient.util';

interface PatientTreatmentEpisodeListProps {
  episodes: GetTreatmentEpisodesResponse;
  id: string;
}

const PatientTreatmentEpisodeList: React.FC<
  PatientTreatmentEpisodeListProps
> = ({ episodes, id }) => {
  const { setComponentVisibility } = useComponentPortalContext();

  if (!episodes?.data.length) {
    return (
      <EmptySectionCard
        caption="There are no treatment episodes associated with this patient"
        id={id}
      />
    );
  }

  return (
    <>
      {episodes?.data.map((episode) => {
        const numWeeks = episode.admitDate
          ? getDiffInWeeks(
              episode.admitDate,
              episode.closedDate,
              DateFormat.shortYearMonthDay,
            )
          : getDiffInWeeks(
              episode.closedDate,
              new Date().toISOString().slice(0, 10),
              DateFormat.shortYearMonthDay,
            );

        return (
          <TreatmentEpisodeCard
            admitDate={toLocalMidnight(new Date(episode.admitDate))}
            closedDate={
              episode.closedDate
                ? toLocalMidnight(new Date(episode.closedDate))
                : null
            }
            id={id}
            key={episode.treatmentEpisodeId}
            level={
              episode.level && {
                color: levelTagColorMapper[episode.level.toLowerCase()],
                id: episode.level,
                value: episode.level,
              }
            }
            numWeeks={numWeeks}
            onClick={() => {
              setComponentVisibility({
                component: PortalComponent.TREATMENT_EPISODE_ACCORDION,
                data: {
                  patientExternalId: episode.patientExternalId,
                  treatmentEpisodeId: episode.treatmentEpisodeId,
                },
                isExpanded: true,
                isVisible: true,
              });
            }}
            status={{
              color:
                treatmentEpisodeColorMapper[
                  TreatmentEpisodeStatus[episode.status.code]
                ],
              id: episode.status.code,
              value: episode.status.description,
            }}
            treatmentProgram={
              episode?.currentTreatmentProgramName
                ? episode.currentTreatmentProgramName
                : episode.treatmentProgramName
            }
          />
        );
      })}
    </>
  );
};

export default PatientTreatmentEpisodeList;
